import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { IntercomService } from '@app/core/plugins/intercom.service';
import {
    EditorSaveStateService,
    EditorSaveStatus,
    EditorStateService,
    ICreativeSaveState
} from '@app/design-view/services';
import { environment } from '@app/environments/environment';
import { DesignViewComponent } from '@app/design-view/design-view.component';
import { CreativesetDataService } from '@app/shared/creativeset';
import { UserService } from '@app/shared/user/state/user.service';
import { VersionsService } from '@app/shared/versions/state/versions.service';
import { UIButtonComponent, UINotificationService } from '@bannerflow/ui';
import { IVersion } from '@studio/domain/creativeset/version';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type SaveButtonDisplayText = 'SAVE' | 'SAVED';

@Component({
    selector: 'editor-topbar',
    templateUrl: './editor-topbar.component.html',
    styleUrls: ['./editor-topbar.component.scss']
})
export class EditorTopbarComponent implements OnInit {
    @HostListener('mousedown') onMouseDown = (): void => this.editor.workspace.transform.cancel();
    @ViewChild('saveButton') saveButton: UIButtonComponent;
    @Input() hideButtons: boolean;
    @Input() id: string;
    showButtons = false;
    contextMenuBorder = true;
    isProd = environment.stage === 'production';
    isLocal = !environment.production;
    creativeset: ICreativeset;
    saveButtonDisplayText: SaveButtonDisplayText = 'SAVED';
    creativeId: string;
    isEmployee$: Observable<boolean>;
    showUploadButton$ = this.editorSaveStateService.isUploading$;
    disabled$ = this.editorSaveStateService.state$.pipe(map(({ disabled }) => disabled));
    loading$ = this.editorSaveStateService.state$.pipe(
        map(({ status }) => status !== EditorSaveStatus.Idle)
    );
    CreativeSaveStatus = EditorSaveStatus;

    private selectedVersion?: IVersion;

    constructor(
        public editor: DesignViewComponent,
        private editorState: EditorStateService,
        public notification: UINotificationService,
        public creativesetData: CreativesetDataService,
        private editorSaveStateService: EditorSaveStateService,
        public userService: UserService,
        private httpClient: HttpClient,
        private intercomService: IntercomService,
        private versionsService: VersionsService
    ) {
        this.isEmployee$ = this.userService.isEmployee$;
        this.creativeId = this.editorState.creative.id;

        this.editorSaveStateService.state$.pipe(takeUntilDestroyed()).subscribe(({ disabled }) => {
            this.updateSaveButtonState(disabled);
        });

        this.versionsService.selectedVersion$.pipe(takeUntilDestroyed()).subscribe(selectedVersion => {
            this.selectedVersion = selectedVersion;
        });

        this.editorSaveStateService.save$
            .pipe(
                withLatestFrom(this.editorSaveStateService.state$),
                map(([_, saveState]) => saveState),
                takeUntilDestroyed()
            )
            .subscribe(saveState => {
                this.onSaveCreative(saveState);
            });
    }

    ngOnInit(): void {
        this.creativeset = this.creativesetData.creativeset;
    }

    onSaveButtonClick(): void {
        this.editorSaveStateService.save({
            saveAll: false,
            origin: 'Design View',
            saveAndExit: true
        });
    }

    onIntercom(): void {
        this.intercomService.showNewMessage();
    }

    onIntercomNews(): void {
        this.intercomService.showIntercomNews();
    }

    private onSaveCreative(saveState: ICreativeSaveState): void {
        if (saveState.status === EditorSaveStatus.Uploading) {
            return this.notification.open('Please wait until upload is finished.', {
                type: 'info',
                placement: 'top',
                autoCloseDelay: 5000
            });
        }
    }

    exit(): void {
        this.editor.exit();
    }

    async openCreativeUrl(): Promise<void> {
        const selectedCreative = this.creativeset.creatives.find(
            creative =>
                creative.version.id === this.selectedVersion?.id &&
                creative.size.id === this.editorState.size.id
        )!;

        const user = (await firstValueFrom(this.userService.oidcUser$))!;
        const result = (await lastValueFrom(
            this.httpClient.get(
                `${environment.origins.acg}/preview-url?creativeset=${this.creativeset.id}&creative=${selectedCreative.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.access_token}`
                    }
                }
            )
        )) as { previewUrl: string };
        window.open(result.previewUrl, '_blank');
    }

    openMetadataTestUrl(): void {
        window.open(this.editor.getMetadataTestUrl(), '_blank');
    }

    onVersionPickerOpen(): void {
        this.editor.versionPickerIsOpen = true;
    }

    onVersionPickerClose(): void {
        this.editor.versionPickerIsOpen = false;
    }

    private updateSaveButtonState(isDisabled: boolean): void {
        this.saveButtonDisplayText = isDisabled ? 'SAVED' : 'SAVE';
    }
}
